export function ProductPage() {
    return (
        <>
            {/* Hero Section */}
            <section className="hero-section">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="hero-content">
                                <h1 className="display-4 fw-bold mb-4">
                                    Arcane ECM
                                </h1>
                                <p className="lead mb-4">
                                    Store files outside of Salesforce while using the seamless Salesforce UI for upload and download.
                                    Files appear to be stored within Salesforce, eliminating file storage costs and size limitations.
                                </p>
                                <div className="d-flex gap-3 flex-wrap">
                                    <a className="btn btn-modern btn-lg" href="/login">
                                        Get Started
                                    </a>
                                    <a className="btn btn-outline-modern btn-lg" href="#features">
                                        Learn More
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 mt-5 mt-lg-0">
                            <div className="text-center">
                                <div className="feature-card bg-white text-dark p-4">
                                    <div className="feature-icon">
                                        <i className="bi bi-diagram-3"></i>
                                    </div>
                                    <h4 className="fw-semibold mb-3">Salesforce Native UI</h4>
                                    <p className="mb-0">Custom LWC components that integrate seamlessly with standard Salesforce file upload and data tables</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Overview */}
            <section id="features" className="py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center mb-5">
                            <h2 className="display-5 fw-semibold mb-3">
                                Salesforce-Native File Management
                            </h2>
                            <p className="lead text-muted">
                                Use standard Salesforce UI elements for file operations while storing files externally.
                                Metadata stays in Salesforce, files transfer to your target system without Salesforce server limitations.
                            </p>
                        </div>
                    </div>
                    <div className="row g-4">
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-currency-dollar"></i>
                                </div>
                                <h3>Eliminate Storage Costs</h3>
                                <p>
                                    Store files externally while using Salesforce UI. File size is not subject to
                                    Salesforce file storage costs or limitations, significantly reducing expenses.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-ui-checks"></i>
                                </div>
                                <h3>Native Salesforce UI</h3>
                                <p>
                                    Custom LWC components that can be placed on any Salesforce Entity page.
                                    Standard file upload, data tables, and related lists for familiar user experience.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-lightning"></i>
                                </div>
                                <h3>No Server Limitations</h3>
                                <p>
                                    Files transfer directly without using Salesforce server-side processing.
                                    Avoid heap size and call-out timeout limits. Upload limits constrained only by target system.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-shield-check"></i>
                                </div>
                                <h3>Advanced Security</h3>
                                <p>
                                    OAuth2 verification on each file download ensures users can only access files
                                    associated with Salesforce records they have permission to view.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-puzzle"></i>
                                </div>
                                <h3>Rich Extensions</h3>
                                <p>
                                    Leverage target system capabilities: AI document processing, watermarking,
                                    anti-virus checking, ML processing, and advanced file operations.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="feature-card">
                                <div className="feature-icon">
                                    <i className="bi bi-database"></i>
                                </div>
                                <h3>Metadata in Salesforce</h3>
                                <p>
                                    File metadata remains in Salesforce for easy access via standard UI.
                                    Files seamlessly integrated with Salesforce records and workflows.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Case: Salesforce-Native File Management */}
            <section className="py-5 bg-light">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center mb-5">
                            <h2 className="display-5 fw-semibold mb-3">
                                Salesforce-Native File Management
                            </h2>
                            <p className="lead text-muted">
                                Upload and manage files using familiar Salesforce UI while storing them externally to eliminate storage costs
                            </p>
                        </div>
                    </div>

                    {/* Workflow Diagram */}
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-xl-10">
                            <div className="card shadow-lg border-0 rounded-3 p-4">
                                <div className="workflow-diagram">
                                    {/* Step 1: Salesforce UI Upload */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-primary">
                                            <i className="bi bi-upload text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">Salesforce UI Upload</h5>
                                        <p className="text-muted small">Standard Salesforce file component</p>
                                        <div className="step-arrow">
                                            <i className="bi bi-arrow-right text-primary"></i>
                                        </div>
                                    </div>

                                    {/* Step 2: Immediate Transfer */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-success">
                                            <i className="bi bi-arrow-right-circle text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">Direct Transfer</h5>
                                        <p className="text-muted small">Files immediately move to target system</p>
                                        <div className="step-arrow">
                                            <i className="bi bi-arrow-right text-success"></i>
                                        </div>
                                    </div>

                                    {/* Step 3: External Storage */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-warning">
                                            <i className="bi bi-cloud text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">External Storage</h5>
                                        <p className="text-muted small">AWS, GCS, Azure, SharePoint</p>
                                        <div className="step-arrow">
                                            <i className="bi bi-arrow-right text-warning"></i>
                                        </div>
                                    </div>

                                    {/* Step 4: Metadata in Salesforce */}
                                    <div className="workflow-step">
                                        <div className="step-icon bg-info">
                                            <i className="bi bi-database text-white"></i>
                                        </div>
                                        <h5 className="fw-semibold mt-3">Metadata in Salesforce</h5>
                                        <p className="text-muted small">Accessible via standard UI</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Use Case Description */}
                    <div className="row">
                        <div className="col-lg-6 mb-4">
                            <div className="card h-100 border-0 shadow">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="feature-icon me-3" style={{width: '3rem', height: '3rem', fontSize: '1.2rem'}}>
                                            <i className="bi bi-ui-checks"></i>
                                        </div>
                                        <h4 className="fw-semibold mb-0">LWC Component Integration</h4>
                                    </div>
                                    <p className="text-muted">
                                        Place custom LWC components on any Salesforce Entity page (Account, Contact, Opportunity, etc.).
                                        Users see standard Salesforce file upload components, data tables, and related lists while
                                        files are stored externally.
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Standard Salesforce UI elements
                                        </li>
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Deploy on any Entity page
                                        </li>
                                        <li className="d-flex align-items-center">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Familiar user experience
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 mb-4">
                            <div className="card h-100 border-0 shadow">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="feature-icon me-3" style={{width: '3rem', height: '3rem', fontSize: '1.2rem'}}>
                                            <i className="bi bi-shield-lock"></i>
                                        </div>
                                        <h4 className="fw-semibold mb-0">Advanced Security Model</h4>
                                    </div>
                                    <p className="text-muted">
                                        OAuth2 verification on each file download ensures users can only access files
                                        associated with Salesforce records they have permission to view. Security is
                                        validated in real-time for every file access.
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Real-time OAuth2 verification
                                        </li>
                                        <li className="d-flex align-items-center mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Record-level permissions
                                        </li>
                                        <li className="d-flex align-items-center">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Per-download security check
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Technical Implementation */}
                    <div className="row mt-5">
                        <div className="col-12">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-4">
                                    <h4 className="fw-semibold mb-4 text-center">Architecture Benefits</h4>
                                    <div className="row">
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-ui-checks text-primary fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">1. Native UI</h6>
                                            <p className="small text-muted">Standard Salesforce upload components and data tables</p>
                                        </div>
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-lightning text-success fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">2. Direct Transfer</h6>
                                            <p className="small text-muted">Files bypass Salesforce server-side processing</p>
                                        </div>
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-currency-dollar text-warning fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">3. Cost Savings</h6>
                                            <p className="small text-muted">No Salesforce storage costs or file size limits</p>
                                        </div>
                                        <div className="col-md-3 text-center mb-3">
                                            <div className="bg-light rounded-circle p-3 d-inline-flex mb-3">
                                                <i className="bi bi-shield-check text-info fs-4"></i>
                                            </div>
                                            <h6 className="fw-semibold">4. OAuth2 Security</h6>
                                            <p className="small text-muted">Real-time permission verification per file access</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center mb-5">
                            <h2 className="display-5 fw-semibold mb-3">
                                Reduce Salesforce Storage Costs
                            </h2>
                            <p className="lead text-muted">
                                Maintain the familiar Salesforce experience while eliminating file storage costs and size limitations
                            </p>
                        </div>
                    </div>
                    <div className="row g-4">
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-primary fw-bold mb-2">100%</div>
                                <h5 className="fw-semibold">Salesforce UI</h5>
                                <p className="text-muted">Native file upload and data tables</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-success fw-bold mb-2">0$</div>
                                <h5 className="fw-semibold">Storage Costs</h5>
                                <p className="text-muted">No Salesforce file storage fees</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-warning fw-bold mb-2">∞</div>
                                <h5 className="fw-semibold">File Size</h5>
                                <p className="text-muted">Target system upload limits only</p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <div className="text-center">
                                <div className="display-4 text-info fw-bold mb-2">100%</div>
                                <h5 className="fw-semibold">Security</h5>
                                <p className="text-muted">OAuth2 per-file verification</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-5 bg-light">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center">
                            <h2 className="fw-semibold mb-3">Ready to Eliminate Salesforce Storage Costs?</h2>
                            <p className="lead text-muted mb-4">
                                Keep the familiar Salesforce experience while storing files externally with advanced security and unlimited capabilities.
                            </p>
                            <div className="d-flex gap-3 justify-content-center flex-wrap">
                                <a className="btn btn-modern btn-lg" href="/login">
                                    Start Free Trial
                                </a>
                                <a
                                    className="btn btn-outline-modern btn-lg"
                                    href="https://www.arcanescale.com/contact-us"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Contact Sales
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}